import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAtomValue } from 'jotai'
import { HelpCircle } from 'lucide-react'
import './Dashboard.scss'
import Sidebar from '../../components/Sidebar/Sidebar'
import { drumtaleAdminSessionAtom } from '../../AtomStore'
import { openPageSpecification } from '../shared/pageSpecification'
import {
  cognitiveContentStatus,
  dashboardDataModelGuide,
  dashboardImplementationGuide,
  dashboardSpecification,
  rankingItems,
  scoreContentStatus,
  subscriptionMix,
} from './dashboardData'

const TOAST_DURATION_MS = 2400

function Dashboard() {
  const navigate = useNavigate()
  const isAdminSessionActive = useAtomValue(drumtaleAdminSessionAtom)

  const [toastMessage, setToastMessage] = useState('')
  const [isToastVisible, setIsToastVisible] = useState(false)
  const toastTimerRef = useRef<number | null>(null)

  useEffect(() => {
    if (!isAdminSessionActive) {
      navigate('/')
    }
  }, [isAdminSessionActive, navigate])

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current)
      }
    }
  }, [])

  const showToast = (message: string) => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current)
    }
    setToastMessage(message)
    setIsToastVisible(true)
    toastTimerRef.current = window.setTimeout(() => setIsToastVisible(false), TOAST_DURATION_MS)
  }

  const handleOpenSpecification = () => {
    const opened = openPageSpecification(dashboardSpecification, dashboardDataModelGuide, dashboardImplementationGuide)
    if (!opened) {
      showToast('화면 사양서 창을 열 수 없습니다. 팝업 차단을 해제해 주세요.')
    }
  }

  return (
    <div className='app-shell'>
      <Sidebar />

      <main className='workspace'>
        <header className='topbar'>
          <div className='title-area'>
            <h1>대시보드</h1>
          </div>
        </header>

        <section className='view active'>
          <div className='metric-grid'>
            <article className='metric-card'>
              <span>오늘 악보 플레이</span>
              <strong>642</strong>
              <small className='up'>전일 대비 8.4% 증가</small>
            </article>
            <article className='metric-card'>
              <span>오늘 신규 가입</span>
              <strong>38</strong>
              <small>카카오 38 · iOS 추후 적용</small>
            </article>
          </div>

          <div className='split-layout'>
            <section className='panel'>
              <div className='panel-header'>
                <div>
                  <h2>활성 구독 구성</h2>
                  <p>현재 구독 중인 회원 등급 비중입니다.</p>
                </div>
              </div>
              <div className='dashboard-list'>
                {subscriptionMix.map(({ grade, memo, count, percentage }) => (
                  <div className='dashboard-list-item' key={grade}>
                    <div>
                      <strong>{grade}</strong>
                      <small>{memo}</small>
                    </div>
                    <div className='dashboard-meter'>
                      <div className='progress'>
                        <span style={{ width: `${percentage}%` }} />
                      </div>
                      <strong>{count.toLocaleString()}명</strong>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className='panel'>
              <div className='panel-header'>
                <div>
                  <h2>인기 콘텐츠</h2>
                  <p>이번 달 기준 플레이 순위입니다.</p>
                </div>
                <div className='period-range'>
                  <select className='filter period-filter' defaultValue='month'>
                    <option value='day'>일간</option>
                    <option value='week'>주간</option>
                    <option value='month'>월간</option>
                  </select>
                  <input className='date-filter' type='date' defaultValue='2026-07-01' />
                  <span>~</span>
                  <input className='date-filter' type='date' defaultValue='2026-07-28' />
                </div>
              </div>
              <div className='ranking-list'>
                {rankingItems.map(({ title, subtitle, plays, badgeLabel, badgeType }, index) => (
                  <div className='rank-item' key={title}>
                    <div>
                      <strong>{index + 1}. {title}</strong>
                      <small>{subtitle} · {plays.toLocaleString()}회</small>
                    </div>
                    <span className={`badge ${badgeType}`}>{badgeLabel}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <section className='panel dashboard-content-panel'>
            <div className='panel-header'>
              <div>
                <h2>콘텐츠 등록 현황</h2>
                <p>현재 등록된 악보와 인지 학습의 노출·무료 이용 상태를 확인합니다.</p>
              </div>
            </div>
            <div className='content-status-grid'>
              <article className='content-status-card'>
                <h3>악보</h3>
                <p>곡 단위 등록 및 사용자 노출 상태</p>
                <div className='content-counts'>
                  <div><span>전체</span><strong>{scoreContentStatus.total}개</strong></div>
                  <div><span>공개</span><strong>{scoreContentStatus.public}개</strong></div>
                  <div><span>미공개</span><strong>{scoreContentStatus.private}개</strong></div>
                  <div><span>무료 공개</span><strong>{scoreContentStatus.free}개</strong></div>
                </div>
                <div className='category-count-list'>
                  {scoreContentStatus.categories.map(({ name, count }) => (
                    <span key={name}>{name}<strong>{count}곡</strong></span>
                  ))}
                </div>
              </article>
              <article className='content-status-card'>
                <h3>인지 학습</h3>
                <p>학습 콘텐츠 단위 등록 및 단계 접근 상태</p>
                <div className='content-counts'>
                  <div><span>전체</span><strong>{cognitiveContentStatus.total}개</strong></div>
                  <div><span>공개</span><strong>{cognitiveContentStatus.public}개</strong></div>
                  <div><span>미공개</span><strong>{cognitiveContentStatus.private}개</strong></div>
                  <div><span>무료 공개</span><strong>{cognitiveContentStatus.free}개</strong></div>
                </div>
              </article>
            </div>
          </section>
        </section>
      </main>

      <button
        className='page-spec-button'
        type='button'
        aria-label='현재 화면 사양서 열기'
        title='현재 화면 사양서'
        onClick={handleOpenSpecification}
      >
        <HelpCircle size={22} />
      </button>

      <div className={`toast${isToastVisible ? ' show' : ''}`} role='status' aria-live='polite'>
        {toastMessage}
      </div>
    </div>
  )
}

export default Dashboard
